import React, {useContext, useEffect, useRef, useState} from 'react';
import SectionHeader from "../../../../util/component/SectionHeader";
import {Box, Button, Dialog, DialogContent, Grid, useTheme} from "@mui/material";
import Loading from "../../common/components/Loading";
import {useTranslation} from "react-i18next";
import AcrissOrderFilter from "./AcrissOrderFilter";
import {useRecoilState} from "recoil";
import {
    acrissOrderFiltersAtom,
    AcrissOrderResponse,
    FormSearchAcrissOrderModel,
    initialAcrissOrderFilters
} from "../../state/acrissOrder";
import {SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {AgGridReact} from "ag-grid-react";
import {useAcrissRepository} from "../../api/repositories/AcrissRepository";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import BootstrapDialogTitle from "../../common/components/BootstrapDialogTitle";
import {FormAutocomplete} from "../../common/form-fields";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import FileUploader from "../../../../util/component/FileUploader";
import i18n from "i18next";

export type FormAcrissOrderModel = {
    legalEntity: SelectElementModelFE | null,
    excelFile?: any;
}

function AcrissOrder() {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // use Theme
    const theme = useTheme();

    // Use Repository
    const {getAllAcriss, exportAcrissOrder, exportLastAcrissOrder, importFileAcrissOrder} = useAcrissRepository()

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Recoil
    const [acrissOrderFilters, setAcrissOrderFilters] = useRecoilState(acrissOrderFiltersAtom);

    // Use State
    const [templateOpen, setTemplateOpen] = useState<boolean>(false);
    const [importOpen, setImportOpen] = useState<boolean>(false);
    const [lastLoadOpen, setLastLoadOpen] = useState<boolean>(false);
    const [initialValues, setInitialValues] = useState<FormAcrissOrderModel>({
        legalEntity: null,
        excelFile: null
    });

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchAcrissOrderModel>({legalEntities: everyLegalEntitiesWithoutAll})

    // Use Grid Data Custom Hook
    const {
        setServerSideDatasource,
        handleTriggerFilters,
        refresh
    } = useAgGridCustomHook<AcrissOrderResponse[], FormSearchAcrissOrderModel>({
        gridRef,
        api: getAllAcriss,
        initialSortColId: "classification",
        entityFilters: testFilters,
        customSort: "AcrissOrderEntityResponse"
    });

    // ************ FORM - START ************ //
    const validationSchema = yup
        .object()
        .shape({
            legalEntity: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
        })
        .defined();

    // Use Form
    const form = useForm<FormAcrissOrderModel>({
        defaultValues: initialValues,
        resolver: yupResolver(
            validationSchema,
            {},
            {
                rawValues: true,
            }
        ),
        shouldUnregister: true,
    });
    // ************ FORM - END ************ //

    useEffect(() => {
        if (gridRef?.current) {
            handleTriggerFilters({
                legalEntities: ((testFilters.legalEntities as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? everyLegalEntitiesWithoutAll
                    : ((testFilters.legalEntities as SelectElementModelFE)
                        ?.code as string),
            });
        }
    }, []);

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        setTestFilters(initialAcrissOrderFilters);
        handleTriggerFilters({
            legalEntities: initialAcrissOrderFilters.legalEntities
        });
    };

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            legalEntities:
                ((testFilters.legalEntities as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? everyLegalEntitiesWithoutAll
                    : ((testFilters.legalEntities as SelectElementModelFE)
                        ?.code as string),
        });
    };

    useEffect(() => {
        setTimeout(() => {
            handleTriggerFiltersInitial();
        }, 500);
    }, [i18n.resolvedLanguage]);

    const columnDefs: ColDef[] = [
        {
            field: "legalEntities",
            headerName: "Legal Entity",
            headerTooltip: "Legal Entity",
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.legalEntity)),
            hide: user.legalEntities.length === 1 ? true : false
        },
        {
            field: "classification",
            headerName: "Classification",
            headerTooltip: "Classification",
            flex: 1,
            minWidth: 100,
            filter: "text",
        },
        {
            field: "acriss",
            headerName: "Acriss/Sipp",
            headerTooltip: "Acriss/Sipp",
            flex: 1,
            minWidth: 100,
            filter: "text",
        },
        {
            field: "description",
            headerName: t("pricing:ancillaryLevelOne.form.labels.description"),
            headerTooltip: t("pricing:ancillaryLevelOne.form.labels.description"),
            flex: 1,
            minWidth: 100,
            filter: "text"
        }
    ];

    const defaultColDef = DEFAULT_COL_DEF;

    const toolbar = {
        renderActions: () => (
            <>
                <Button fullWidth size="small" onClick={() => setTemplateOpen(true)}>Template</Button>
                <Button fullWidth size="small" onClick={() => setImportOpen(true)}>{t("pricing:acrissOrder.import")}</Button>
            </>
        )
    }

    const handleConfirmButton = async () => {
        const formFields = form.getValues()
        const legalEntity = await form.trigger("legalEntity");

        if (legalEntity && templateOpen) {
            await exportAcrissOrder((formFields.legalEntity as SelectElementModelFE).code as string)
            setTemplateOpen(false)
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } else if (legalEntity && importOpen) {
            if (form.getValues("excelFile") != null) {
                dispatchGlobalData(SET_SNACKBAR.LOADING)
                await importFileAcrissOrder((formFields.legalEntity as SelectElementModelFE).code as string, formFields.excelFile)
                setImportOpen(false)
                form.reset(initialValues)
                dispatchGlobalData(SET_SNACKBAR.SUCCESS)
                refresh();
            } else {
                dispatchGlobalData(SET_SNACKBAR.FORM_ERROR)
            }
        } else if (legalEntity && lastLoadOpen) {
            await exportLastAcrissOrder((formFields.legalEntity as SelectElementModelFE).code as string)
            setLastLoadOpen(false)
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        }
    }

    const handleClosePopUp = () => {
        setTemplateOpen(false)
        setImportOpen(false)
        setLastLoadOpen(false)
    }

    useEffect(() => {
        console.log(form.getValues())
    }, [form.getValues()])

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_ACRISS_ORDER.name")}/>
            <AcrissOrderFilter
                handleGetRateList={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
            />
            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={toolbar}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={setServerSideDatasource}
                    />

                </Grid>
            </Grid>
            <Button style={{marginLeft: "15px"}} size="small" onClick={() => setLastLoadOpen(true)}>{t("pricing:acrissOrder.lastLoad")}</Button>

            <Dialog open={templateOpen || importOpen || lastLoadOpen} maxWidth={"xl"} fullWidth>
                <BootstrapDialogTitle id="customized-dialog-title" end={'end'}
                                      onClose={handleClosePopUp}></BootstrapDialogTitle>
                <DialogContent style={{color: theme.palette.primary.main}}>
                    <FormProvider {...form}>
                        <Grid container spacing={2} rowSpacing={3} alignItems="start">
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...form.register(`legalEntity`)}
                                    label={`Legal Entity *`}
                                    options={optionsFiltered}
                                    isOptionEqualToValue={(
                                        option: SelectElementModelFE,
                                        value: SelectElementModelFE
                                    ) => option.code === value.code}
                                />
                            </Grid>
                            {importOpen && <Grid item xs={12} md={3}>
                                <FileUploader onUpload={(file: File) => form.setValue("excelFile", file)}
                                              onRemove={() => form.setValue("excelFile", null)}
                                              acceptedFiles={{"text/xls": [".xls", ".xlsx"]}}/>
                            </Grid>}
                        </Grid>
                    </FormProvider>
                    <div style={{display: "flex", justifyContent: "end", alignItems: "end"}}>
                        <Button style={{marginLeft: "15px"}} size="small"
                                onClick={handleConfirmButton}>{t("pricing:alert.buttons.confirm")}</Button>
                    </div>
                </DialogContent>
            </Dialog>
            <Loading/>
        </Box>
    );
}

export default AcrissOrder;